import { type FC, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import isEqual from "lodash/isEqual";

import UploadImage from "../upload/UploadImage";
import { Modal } from "../../../modal/index";
import fileUtils, { type OnUploadProgressHandler, type AcceptCancelationHandler } from "utils/fileUtils";
import ImageUploaderModalButtons from "../upload/ImageUploaderModalButtons/ImageUploaderModalButtons";
import ModalSizes from "enums/modalSizes";
import Tabs from "../../../tabs/Tabs";

import "./thumbnailUploaderModal.scss";

const defaultActiveIndex = 0;

export interface ThumbnailUploaderModalProps {
  trigger?: (openModal: () => void) => ReactNode;
  onImageChange?: (uri: string, fileName: string) => void;
  entityId?: number;
  sasLinkProviderAsync: () => Promise<{ uri: string; [key: string]: unknown }>;
  videosActions: { changeImageProcessingState: (processing: boolean) => void };
  generateThumbnail: (sas: { uri: string; [key: string]: unknown }) => void;
  textTracks?: unknown[];
  areTextTracksLoading?: boolean;
  renderField: () => ReactNode;
  assetManifests?: unknown;
}

export const ThumbnailUploaderModal: FC<ThumbnailUploaderModalProps> = (props) => {
  const { assetManifests, videosActions, sasLinkProviderAsync, generateThumbnail, renderField, trigger } = props;

  const [croppedImage, setCroppedImage] = useState<Blob | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isLooksGoodButtonVisible, setIsLooksGoodButtonVisible] = useState<boolean>(false);
  const [thumbnails, setThumbnails] = useState<unknown[]>([]);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const [fileName, setFileName] = useState<File[]>([]);

  const closeModalRef = useRef<(() => void) | null>(null);

  const prevAssetManifestsRef = useRef(assetManifests);
  useEffect(() => {
    if (!isEqual(assetManifests, prevAssetManifestsRef.current)) {
      setThumbnails([]);
      prevAssetManifestsRef.current = assetManifests;
    }
  }, [assetManifests]);

  const handleCroppedImageChange = useCallback((image: Blob | null) => {
    setCroppedImage(image);
    setIsLooksGoodButtonVisible(image !== null);
  }, []);

  const handleSetFileName = useCallback((files: FileList) => {
    setFileName(Array.from(files));
  }, []);

  const handleSetSelectedFile = useCallback((file: ArrayBuffer | string | null) => {
    setSelectedFile(typeof file === "string" ? file : null);
  }, []);

  const resetState = useCallback(() => {
    setThumbnails([]);
    setIsLooksGoodButtonVisible(false);
    setSelectedIndex(0);
    setCroppedImage(null);
    setSelectedFile(null);
    setFileName([]);
  }, []);

  const closeModalWindow = useCallback(
    (closeModal?: () => void) => {
      resetState();
      (closeModal || closeModalRef.current)?.();
    },
    [resetState],
  );

  const generateThumbnailHandler = useCallback(async () => {
    if (!croppedImage) return;
    setIsUploading(true);
    videosActions.changeImageProcessingState(true);
    const uploadOptions = { contentType: croppedImage.type };
    const sas = await sasLinkProviderAsync();

    const fileToUpload = new File([croppedImage], fileName[0]?.name ?? "thumbnail.jpg", { type: croppedImage.type });
    const noopProgress: OnUploadProgressHandler = () => {};
    const noopAccept: AcceptCancelationHandler = () => {};
    await fileUtils
      .uploadFileToBlob(sas.uri, fileToUpload, noopProgress, noopAccept, uploadOptions)
      .then(() => {
        setIsUploading(false);
        setThumbnails([]);
        setSelectedIndex(0);
        generateThumbnail(sas);
        closeModalRef.current && closeModalRef.current();
      })
      .catch(() => {
        setIsUploading(false);
      });
  }, [croppedImage, videosActions, sasLinkProviderAsync, fileName, generateThumbnail]);

  type TabChangeData = { activeIndex: number };
  const handleTabChanged = useCallback(
    (_: unknown, data: TabChangeData) => {
      const { activeIndex } = data;
      if (activeIndex === 0) {
        setIsLooksGoodButtonVisible(thumbnails.length > 0);
        setSelectedIndex(0);
      }
      if (activeIndex === 1) {
        setIsLooksGoodButtonVisible(croppedImage !== null);
        setSelectedIndex(1);
      }
    },
    [thumbnails.length, croppedImage],
  );

  const renderModalActions = useCallback(
    (closeModal: () => void) => {
      closeModalRef.current = closeModal;
      const handleLooksGood = selectedIndex === 0 ? () => closeModalWindow(closeModal) : generateThumbnailHandler;
      const canSubmit = selectedIndex === 0 || isLooksGoodButtonVisible;
      const confirmationText = selectedIndex === 0 ? "Done" : "Save";

      return (
        <ImageUploaderModalButtons
          closeModal={() => closeModalWindow(closeModal)}
          handleLooksGood={handleLooksGood}
          isUploading={isUploading}
          canSubmit={canSubmit}
          confirmationText={confirmationText}
        />
      );
    },
    [closeModalWindow, generateThumbnailHandler, isUploading, isLooksGoodButtonVisible, selectedIndex],
  );

  return (
    <Modal
      title="Upload Video"
      renderTrigger={trigger}
      className="upload-image-modal"
      renderModalActions={renderModalActions}
      onCancel={() => closeModalWindow()}
      size={ModalSizes.large}
    >
      <Tabs defaultActiveIndex={defaultActiveIndex} onTabChange={handleTabChanged}>
        <Tabs.Pane label="Video">
          <div className="video-upload-section upload-video">{renderField()}</div>
        </Tabs.Pane>
        <Tabs.Pane label="Thumbnail">
          <div className="image-upload-section upload-image">
            <UploadImage
              onCroppedImageChange={handleCroppedImageChange}
              file={selectedFile || ""}
              onFileChanged={handleSetSelectedFile}
              fileName={fileName}
              onFileNameChanged={handleSetFileName}
            />
          </div>
        </Tabs.Pane>
      </Tabs>
    </Modal>
  );
};

export default ThumbnailUploaderModal;
